// src/features/admin/pages/AdminBooksPage.tsx
import React, { useEffect, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";

type SourceItem    = { source: string; count: number };
type MonthItem     = { month: string; count: number };
type Summary       = { total: number; new_this_month: number; by_source: SourceItem[] };
type ReviewSummary = { total: number; this_month: number };
type TopReview     = {
    id: number; content: string; rating: number; likes_count: number;
    created_at: string; nickname: string | null; name: string | null; book_title: string | null;
};

const SOURCE_COLORS = [
    "#18181b", "#2563eb", "#16a34a", "#db2777", "#d97706",
    "#7c3aed", "#0891b2", "#dc2626", "#65a30d", "#9333ea",
];

const DURATION_OPTIONS = [
    { label: "전체", value: 0 },
    { label: "최근 7일", value: 7 },
    { label: "최근 30일", value: 30 },
    { label: "최근 90일", value: 90 },
    { label: "최근 180일", value: 180 },
    { label: "최근 1년", value: 365 },
];

function sourceLabel(s: string) {
    const map: Record<string, string> = {
        naver: "네이버", aladin: "알라딘", kakao: "카카오",
        manual: "직접입력", dreamlab: "드림랩", kolis: "KOLIS",
    };
    return map[s] ?? s;
}

export default function AdminBooksPage() {
    const [summary, setSummary]               = useState<Summary | null>(null);
    const [monthly, setMonthly]               = useState<MonthItem[]>([]);
    const [reviewSummary, setReviewSummary]   = useState<ReviewSummary | null>(null);
    const [topReviews, setTopReviews]         = useState<TopReview[]>([]);
    const [topDays, setTopDays]               = useState(30);
    const [loading, setLoading]               = useState(true);
    const [topLoading, setTopLoading]         = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api("/admin/books/summary"),
            api("/admin/books/monthly?months=12"),
            api("/admin/books/review-summary"),
        ]).then(([s, m, rs]) => {
            setSummary(s);
            setMonthly(m.items || []);
            setReviewSummary(rs);
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setTopLoading(true);
        api(`/admin/books/top-reviews?days=${topDays}&limit=10`)
            .then(d => setTopReviews(d.items || []))
            .catch(console.error)
            .finally(() => setTopLoading(false));
    }, [topDays]);

    const monthlyData = monthly.map(r => ({ ...r, label: r.month.slice(2) }));

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: FW.extraBold }}>도서 통계</h2>
                <span style={{ fontSize: 13, color: COLOR.textMuted, fontWeight: 500 }}>
                    {new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "short" })}
                </span>
            </div>

            {/* Book summary cards */}
            {summary && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                    <StatCard label="전체 도서 수" value={summary.total} color="#18181b" />
                    <StatCard label="이번 달 신규 도서" value={summary.new_this_month} color="#2563eb" />
                    <StatCard label="출처 종류" value={summary.by_source.length} color="#16a34a" unit="종" />
                </div>
            )}

            {/* Review summary cards */}
            {reviewSummary && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
                    <StatCard label="전체 리뷰 수" value={reviewSummary.total} color="#7c3aed" unit="개" />
                    <StatCard label="이번 달 신규 리뷰" value={reviewSummary.this_month} color="#db2777" unit="개" />
                </div>
            )}

            {/* Monthly new books */}
            <Section title="월별 신규 도서 수 (최근 12개월)">
                {loading ? <Skeleton h={220} /> : (
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={monthlyData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} interval={0} />
                            <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }}
                                cursor={{ fill: "#f4f4f5" }}
                                formatter={(v: number) => [v.toLocaleString(), "신규 도서"]}
                                labelFormatter={l => `월: 20${l}`}
                            />
                            <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Section>

            {/* Source detail table */}
            <Section title="출처별 상세">
                {loading ? <Skeleton h={120} /> : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "#f4f4f5" }}>
                                <th style={th}>출처</th>
                                <th style={th}>도서 수</th>
                                <th style={th}>비율</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(summary?.by_source || []).map((r, i) => {
                                const pct = summary!.total > 0 ? ((r.count / summary!.total) * 100).toFixed(1) : "0.0";
                                return (
                                    <tr key={r.source} style={{ borderBottom: "1px solid #f4f4f5" }}>
                                        <td style={{ ...td, display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: SOURCE_COLORS[i % SOURCE_COLORS.length], display: "inline-block", flexShrink: 0 }} />
                                            {sourceLabel(r.source)}
                                        </td>
                                        <td style={{ ...td, fontWeight: 700 }}>{r.count.toLocaleString()}</td>
                                        <td style={td}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <div style={{ flex: 1, height: 6, background: "#f4f4f5", borderRadius: 4, overflow: "hidden" }}>
                                                    <div style={{ width: `${pct}%`, height: "100%", background: SOURCE_COLORS[i % SOURCE_COLORS.length], borderRadius: 4 }} />
                                                </div>
                                                <span style={{ minWidth: 38, textAlign: "right", color: COLOR.textMuted }}>{pct}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Section>

            {/* Top reviews by likes */}
            <Section
                title="좋아요 상위 리뷰 TOP 10"
                action={
                    <select
                        value={topDays}
                        onChange={e => setTopDays(Number(e.target.value))}
                        style={{ padding: "5px 10px", border: BORDER.lightAlt, borderRadius: RADIUS.sm, fontSize: 12, fontWeight: FW.medium, background: COLOR.white, cursor: "pointer" }}
                    >
                        {DURATION_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                }
            >
                {topLoading ? <Skeleton h={200} /> : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "#f4f4f5" }}>
                                <th style={{ ...th, width: 32 }}>#</th>
                                <th style={th}>리뷰 내용</th>
                                <th style={th}>도서</th>
                                <th style={th}>작성자</th>
                                <th style={{ ...th, textAlign: "right" }}>♥ 좋아요</th>
                                <th style={{ ...th, textAlign: "right" }}>별점</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topReviews.map((r, i) => (
                                <tr key={r.id} style={{ borderBottom: "1px solid #f4f4f5" }}>
                                    <td style={{ ...td, color: i < 3 ? "#2563eb" : COLOR.textMuted, fontWeight: FW.extraBold }}>{i + 1}</td>
                                    <td style={{ ...td, maxWidth: 240 }}>
                                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {r.content}
                                        </span>
                                    </td>
                                    <td style={{ ...td, color: COLOR.textMuted, maxWidth: 160 }}>
                                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {r.book_title || "-"}
                                        </span>
                                    </td>
                                    <td style={{ ...td, color: COLOR.textMuted }}>{r.nickname || r.name || "-"}</td>
                                    <td style={{ ...td, fontWeight: FW.bold, textAlign: "right", color: "#db2777" }}>{r.likes_count.toLocaleString()}</td>
                                    <td style={{ ...td, textAlign: "right" }}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</td>
                                </tr>
                            ))}
                            {topReviews.length === 0 && (
                                <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: COLOR.textLight, padding: 24 }}>데이터 없음</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </Section>
        </div>
    );
}

function StatCard({ label, value, color, unit = "권" }: { label: string; value: number; color: string; unit?: string }) {
    return (
        <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, padding: "18px 20px" }}>
            <div style={{ fontSize: 12, fontWeight: FW.medium, color: COLOR.textMuted, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: FW.extraBold, color }}>{value.toLocaleString()}<span style={{ fontSize: 14, fontWeight: FW.medium, marginLeft: 4, color }}>{unit}</span></div>
        </div>
    );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, padding: "20px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: FW.bold, color: COLOR.textDark }}>{title}</div>
                {action}
            </div>
            {children}
        </div>
    );
}

function Skeleton({ h }: { h: number }) {
    return <div style={{ height: h, background: COLOR.bgLight, borderRadius: RADIUS.sm }} />;
}

const th: React.CSSProperties = { padding: "8px 12px", textAlign: "left", fontWeight: FW.bold, color: "#3f3f46" };
const td: React.CSSProperties = { padding: "8px 12px", color: COLOR.textDark };